import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { calendarRequest } from '../../services/calendarService.js';

export default function GoogleCalendarDialog({ open, onClose, onChanged }) {
  const [status, setStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [selected, setSelected] = useState([]);
  const [writeId, setWriteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setBusy(true); setError(''); setStatus(null); setConfirmDisconnect(false);
    (async () => {
      try {
        const data = await calendarRequest('calendar/status', { signal: controller.signal });
        if (controller.signal.aborted) return;
        setStatus(data); setSelected(data.selected_calendar_ids || []); setWriteId(data.write_calendar_id || '');
        if (data.connected && data.status !== 'reconnect_required') {
          const result = await calendarRequest('calendar/calendars', { signal: controller.signal });
          if (!controller.signal.aborted) setCalendars(result.calendars || []);
        } else setCalendars([]);
      } catch (err) { if (!controller.signal.aborted) setError(err.message); }
      finally { if (!controller.signal.aborted) setBusy(false); }
    })();
    return () => controller.abort();
  }, [open]);
  const run = async task => {
    setBusy(true); setError('');
    try { await task(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const connect = () => run(async () => {
    const data = await calendarRequest('calendar/connect', { method: 'POST', body: {} });
    const url = new URL(data.authorization_url);
    if (url.protocol !== 'https:' || url.hostname !== 'accounts.google.com') throw new Error('Google sign-in is unavailable. Please try again.');
    window.location.assign(url.href);
  });
  const save = () => run(async () => {
    await calendarRequest('calendar/calendars', { method: 'PUT', body: { selected_calendar_ids: selected, write_calendar_id: writeId } });
    onChanged(); onClose();
  });
  const disconnect = () => run(async () => {
    await calendarRequest('calendar/disconnect', { method: 'POST', body: {} });
    onChanged(); onClose();
  });
  const reconnect = status?.status === 'reconnect_required';
  return <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
    <DialogTitle>My Google Calendar</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      <Typography>Connect your own Google account. Your organization sees busy times, not private event titles or details.</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {busy && <CircularProgress size={24} aria-label="Loading Google Calendar" />}
      {status && <>
        {status.configured === false && <Alert severity="info">Google Calendar is not available yet. Please contact your administrator.</Alert>}
        <Alert severity={reconnect ? 'warning' : 'info'}>{reconnect ? 'Google access has expired or was revoked. Reconnect to resume syncing.' : status.connected ? 'Google busy times block availability. Scribe appointments sync to your selected Google calendar. Manage appointment changes in Scribe.' : 'Google Calendar is optional. You can still manage your working hours and time off here.'}</Alert>
        {status.sync_error && <Alert severity="warning">The latest sync did not complete. Previously synced busy times may be out of date. Reconnect if Google access has changed.</Alert>}
        {status.connected && !status.last_synced_at && status.selected_calendar_ids?.length > 0 && <Alert severity="warning">The first calendar sync is pending. New bookings will be available after synchronization completes.</Alert>}
        {status.last_synced_at && <Typography variant="body2" color="text.secondary">Last synced: {new Date(status.last_synced_at).toLocaleString()}</Typography>}
        {status.connected && !reconnect && <>
          <Typography variant="subtitle2">Calendars that block my availability (choose 1–10)</Typography>
          {calendars.map(calendar => <FormControlLabel key={calendar.id} control={<Checkbox disabled={busy} checked={selected.includes(calendar.id)} onChange={event => setSelected(ids => event.target.checked ? [...ids, calendar.id] : ids.filter(id => id !== calendar.id))} />} label={calendar.summary || calendar.id} />)}
          <TextField select label="Send Scribe appointments to" value={writeId} onChange={event => setWriteId(event.target.value)} disabled={busy} fullWidth>
            <MenuItem value="" disabled={Boolean(status.write_calendar_id)}>Do not send appointments</MenuItem>
            {calendars.filter(calendar => ['owner', 'writer'].includes(calendar.accessRole)).map(calendar => <MenuItem key={calendar.id} value={calendar.id}>{calendar.summary || calendar.id}</MenuItem>)}
          </TextField>
          <Typography variant="body2" color="text.secondary">To stop sending appointments after enabling it, disconnect Google Calendar. After connecting, updates run in the background. Appointments created or changed by an Admin also sync automatically. Editing an event in Google does not change the booking in Scribe.</Typography>
        </>}
        {confirmDisconnect && <Alert severity="warning">Disconnecting stops future synchronization. Existing events in Google may remain.</Alert>}
      </>}
    </Stack></DialogContent>
    <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
      {(status?.connected || reconnect) && <Button color="error" disabled={busy} onClick={confirmDisconnect ? disconnect : () => setConfirmDisconnect(true)}>{confirmDisconnect ? 'Confirm disconnect' : 'Disconnect'}</Button>}
      <Button disabled={busy} onClick={onClose}>Close</Button>
      {status && (!status.connected || reconnect) && <Button variant="contained" disabled={busy || status.configured === false} onClick={connect}>{reconnect ? 'Reconnect Google Calendar' : 'Connect Google Calendar'}</Button>}
      {status?.connected && !reconnect && <Button variant="contained" disabled={busy || !selected.length || selected.length > 10} onClick={save}>Save calendar settings</Button>}
    </DialogActions>
  </Dialog>;
}
